import { useState } from 'react'

import { WIZARD_SIZES, Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  BulkCreateDocumentsDocumentCustomFieldsInput,
  BulkCreateDocumentsDocumentSigneeStampsInput,
  DocumentsFolderItemFragment,
  EmployeeDocumentItemFragment,
  LegalEntityItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { countryFolderFilter } from 'modules/documents/components/DocumentsAndFolders/FoldersList/legalEntityFolderFilter'
import EsignaturePlacement from 'modules/documents/components/UploadFormWizard/_steps/EsignaturePlacement'
import FileUploader from 'modules/documents/components/UploadFormWizard/_steps/FileUploader'
import Settings from 'modules/documents/components/UploadFormWizard/_steps/Settings'
import { WizardData } from 'modules/documents/components/UploadFormWizard/types'
import useCreateDocuments from 'modules/documents/hooks/documents/useCreateDocuments'
import useFilesStore from 'modules/documents/hooks/files/useFilesStore'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { isWritableFolder } from 'modules/documents/lib/Folders'
import { ESIGNATURE_MAX_FILE_SIZE, MAX_FILE_SIZE, SPACES } from 'modules/documents/lib/constants'

const ESIGNATURE_PLACEMENT_STEP = 2

interface Props {
  space: string
  legalEntity?: LegalEntityItemFragment
  targetEmployee?: EmployeeDocumentItemFragment
  folders: DocumentsFolderItemFragment[]
  selectedFolder?: Folder
  titleDescription?: string
  onClose: () => void
  canUseCompanyDocuments?: boolean
}

type WizardSizes = (typeof WIZARD_SIZES)[number]

const valueToPercentage = (value: number, total: number) => {
  const percentage = Math.round(((value * 100) / total) * 100) / 100
  return Math.max(1, Math.min(100, percentage))
}

const validateFileSize = (files: File[], maxSize: number, message: string) => {
  const isFileSizeValid = files.every((file) => file.size <= maxSize)
  if (!isFileSizeValid) {
    app.setFlash({
      type: 'wrong',
      message,
    })
  }
  return isFileSizeValid
}

const UploadFormWizard = ({
  space,
  targetEmployee,
  legalEntity,
  folders,
  selectedFolder,
  titleDescription,
  onClose,
  canUseCompanyDocuments = false,
}: Props) => {
  const { employee: authorEmployee, access: author, company } = useCurrentSession()
  const isMyDocuments =
    space === SPACES.EMPLOYEE_MY_DOCUMENTS && targetEmployee?.id === authorEmployee?.id
  const isInternalCompany = space === SPACES.COMPANY_INTERNAL
  const { getFiles, deleteAllFiles } = useFilesStore()
  const { createDocuments, loading: submitting } = useCreateDocuments()
  const firstName = targetEmployee?.firstName || ''
  const [signaturePlacementValidations, setSignaturePlacementValidations] = useState(false)
  const canSeeDocuments = targetEmployee?._policies?.seeDocuments.can
  const [size, setSize] = useState<WizardSizes>('regular')

  const initialData: WizardData = {
    files: getFiles(),
    folderId: selectedFolder?.id || null,
    companyId: company.id,
    hidden: false,
    esignatureRequest: false,
    additionalSignersAccessIds: [],
    customFields: [],
    signaturePlacement: false,
    signatureStamps: [],
  }
  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  useUnsafeEffect(() => {
    setWizardData(initialData)
  }, [])

  const writableFolders = legalEntity
    ? countryFolderFilter(legalEntity.country, folders).filter((f) =>
        isWritableFolder(f, canSeeDocuments)
      )
    : []

  const handleOnDataChanged = (newData: WizardData) => {
    const newFiles = newData.files !== wizardData.files
    const newAdditionalSigners =
      newData.additionalSignersAccessIds !== wizardData.additionalSignersAccessIds
    const newSignaturePlacementParams = newFiles || newAdditionalSigners

    if (newSignaturePlacementParams) {
      newData.signatureStamps = []
      setSignaturePlacementValidations(false)
    }

    // This avoid to remove the additionaSigners on adding a new file
    if (newFiles) {
      newData.additionalSignersAccessIds = wizardData.additionalSignersAccessIds
    }

    setWizardData({ ...wizardData, ...newData })
  }

  const handleStepChanged = (step: number) => {
    if (step === ESIGNATURE_PLACEMENT_STEP) {
      setSize('fullscreen')
    } else {
      setSize('regular')
    }
  }

  const submitLabel = () => {
    const { files, esignatureRequest } = wizardData
    const filesCounter = files.length

    if (!filesCounter) {
      return i18n.t('documents.beta.submit.default')
    }

    return esignatureRequest
      ? i18n.tp('documents.beta.submit.esignature', { count: filesCounter })
      : i18n.tp('documents.beta.submit.many', { count: filesCounter })
  }

  const onSubmit = async ({
    files,
    folderId,
    hidden,
    customFields,
    esignatureRequest,
    signaturePlacement,
    additionalSignersAccessIds,
    signatureStamps,
  }: WizardData) => {
    const isMaxSizeValid = validateFileSize(
      files,
      esignatureRequest ? ESIGNATURE_MAX_FILE_SIZE : MAX_FILE_SIZE,
      esignatureRequest
        ? i18n.t('documents.beta.file_errors.max_file_size_esignature')
        : i18n.t('documents.beta.file_errors.max_file_size')
    )

    if (!isMaxSizeValid) return false

    const customFieldsValues: BulkCreateDocumentsDocumentCustomFieldsInput[] = customFields.map(
      (form) => {
        const formData = form.data()
        return {
          resourceId: formData.resource_id,
          value: formData.value ? formData.value.toString() : '',
        }
      }
    )

    const signeeStamps: BulkCreateDocumentsDocumentSigneeStampsInput[] = signatureStamps.map(
      (stamp) => {
        return {
          fileId: stamp.fileId,
          signeeId: stamp.signer.id,
          page: stamp.page.number,
          width: valueToPercentage(stamp.width, stamp.page.width),
          height: valueToPercentage(stamp.height, stamp.page.height),
          top: valueToPercentage(stamp.y, stamp.page.height),
          left: valueToPercentage(stamp.x, stamp.page.width),
        }
      }
    )

    const result = await createDocuments({
      authorId: author.id,
      employeeId: targetEmployee?.id,
      files,
      folderId,
      public: !hidden,
      notify: !hidden,
      space,
      customFields: customFieldsValues,
      signaturePlacement,
      signeeIds: esignatureRequest ? additionalSignersAccessIds : [],
      signeeStamps: esignatureRequest && signaturePlacement ? signeeStamps : [],
    })

    const handled = handleResult(result, () => undefined)

    if (!handled.success) {
      setSignaturePlacementValidations(true)
    }

    if (!handled) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.documents_upload_failed'),
      })
      return
    }

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      deleteAllFiles()
      onClose()
    }
  }

  const fileUploaderStep: WizardStep<WizardData> = {
    title: i18n.t('documents.action'),
    isCompleted: ({ files }) => files.length > 0,
    nextText: i18n.t('common.next'),
    render: (props) => <FileUploader {...props} />,
  }

  const fileSettingsStep: WizardStep<WizardData> = {
    title: i18n.t('split_documents.sidepanel.settings'),
    isCompleted: ({ esignatureRequest, additionalSignersAccessIds }) => {
      return !esignatureRequest || additionalSignersAccessIds.length > 0
    },
    nextText: wizardData.signaturePlacement ? i18n.t('common.next') : submitLabel(),
    render: (props) => (
      <Settings
        {...props}
        folders={writableFolders}
        employeeFirstName={firstName}
        showVisibility={!isMyDocuments && !isInternalCompany}
        space={space}
        canUseCompanyDocuments={canUseCompanyDocuments}
        submitting={submitting}
        defaultSigner={targetEmployee?.access.id}
      />
    ),
  }

  const esignaturePlacementStep: WizardStep<WizardData> = {
    title: i18n.t('documents.beta.form_modal.esignature_placement'),
    hideSteps: true,
    isCompleted: ({ signaturePlacement }) => signaturePlacement,
    nextText: submitLabel(),
    render: (props) => (
      <EsignaturePlacement {...props} showValidations={signaturePlacementValidations} />
    ),
  }

  const steps = [
    fileUploaderStep,
    fileSettingsStep,
    ...(wizardData.signaturePlacement ? [esignaturePlacementStep] : []),
  ]

  fileUploaderStep.footerMessage = i18n.t('documents.beta.form_modal.step_text', {
    current_step: 1,
    total_steps: steps.length,
  })

  fileSettingsStep.footerMessage = i18n.t('documents.beta.form_modal.step_text', {
    current_step: 2,
    total_steps: steps.length,
  })

  if (wizardData.signaturePlacement) {
    esignaturePlacementStep.footerMessage = i18n.t('documents.beta.form_modal.step_text', {
      current_step: 3,
      total_steps: steps.length,
    })
  }

  const onOpenAutoFocus = () => {}

  return (
    <Wizard.Modal
      title={i18n.t('documents.action')}
      initialData={initialData}
      icon="upload"
      description={titleDescription}
      onDataChanged={handleOnDataChanged}
      onStepChanged={handleStepChanged}
      onSubmit={onSubmit}
      onClose={onClose}
      steps={steps}
      size={size}
      onOpenAutoFocus={onOpenAutoFocus}
    />
  )
}

export default UploadFormWizard
